import { useCallback, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { CreateObjectStepId } from 'entities/kyrgyzstan/model/create-object'

import { KyrgyzstanCreateObjectApi } from 'shared/api/kyrgyzstan/create-object'
import mapBg from 'shared/images/map-bg.png'
import { Button } from 'shared/ui/button'
import { useApiErrorDialog } from 'shared/ui/dialog-bottom-sheet/lib'
import { ImageOptimized } from 'shared/ui/image-optimized'
import { Loader } from 'shared/ui/loader'
import { ZoomControls } from 'shared/ui/map/components'

import {
  mapCreateObjectAddressDraftToRequest,
  mapCreateObjectAddressResponseToDraft,
} from '../../lib/address'
import type { CreateObjectAddressDraft } from '../../lib/address'
import type { CreateObjectDraftStepScreenProps } from '../../types'
import { CreateObjectStepLayout } from '../create-object-step-layout'

import { mapAddressDraftToInitialPoint } from './lib/map-address-draft-to-initial-point'
import s from './styles.module.scss'
import { useSelectableMap } from './use-selectable-map'

type AddressMapStepOperation = 'idle' | 'loading' | 'submitting'

interface AddressMapPoint {
  latitude: number
  longitude: number
}

const areAddressMapPointsEqual = (
  leftPoint?: AddressMapPoint | null,
  rightPoint?: AddressMapPoint | null,
) => {
  if (!leftPoint || !rightPoint) {
    return leftPoint === rightPoint
  }

  return (
    leftPoint.latitude === rightPoint.latitude &&
    leftPoint.longitude === rightPoint.longitude
  )
}

export const AddressMapStep = ({
  draftId,
  layoutProps,
  mode = 'create',
  onNext,
  onOpenStep,
}: CreateObjectDraftStepScreenProps) => {
  const { t } = useTranslation()
  const { showApiErrorDialog } = useApiErrorDialog()
  const [addressDraft, setAddressDraft] =
    useState<CreateObjectAddressDraft | null>(null)
  const initialPoint = mapAddressDraftToInitialPoint(addressDraft)
  const {
    isMapLoading,
    mapError,
    mapRef,
    selectedPoint,
    handleRetry,
    handleZoomIn,
    handleZoomOut,
  } = useSelectableMap(initialPoint)
  const [operation, setOperation] = useState<AddressMapStepOperation>('idle')

  useEffect(() => {
    const abortController = new AbortController()

    const fetchAddress = async () => {
      setOperation('loading')

      try {
        const { data } = await KyrgyzstanCreateObjectApi.getAddressStep(
          draftId,
          abortController.signal,
        )
        const nextAddressDraft = mapCreateObjectAddressResponseToDraft(
          data.data,
        )

        if (nextAddressDraft) {
          setAddressDraft(nextAddressDraft)
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          showApiErrorDialog(error)
        }
      } finally {
        if (!abortController.signal.aborted) {
          setOperation('idle')
        }
      }
    }

    fetchAddress()

    return () => {
      abortController.abort()
    }
  }, [draftId, showApiErrorDialog])

  const handleContinue = useCallback(
    async (targetStepId?: CreateObjectStepId) => {
      if (!addressDraft || !selectedPoint || operation !== 'idle') {
        return
      }

      const nextAddressDraft = {
        ...addressDraft,
        latitude: selectedPoint.latitude,
        longitude: selectedPoint.longitude,
      }

      setOperation('submitting')

      try {
        await KyrgyzstanCreateObjectApi.saveAddressStep(
          draftId,
          mapCreateObjectAddressDraftToRequest(nextAddressDraft),
        )

        setAddressDraft(nextAddressDraft)

        if (targetStepId) {
          onOpenStep(targetStepId)

          return
        }

        onNext()
      } catch (error) {
        showApiErrorDialog(error)
      } finally {
        setOperation('idle')
      }
    },
    [
      addressDraft,
      draftId,
      onNext,
      onOpenStep,
      operation,
      selectedPoint,
      showApiErrorDialog,
    ],
  )

  const submitTargetStepId =
    mode === 'edit' ? CreateObjectStepId.Address : undefined
  const hasSelectedPointChanged =
    selectedPoint !== null &&
    !areAddressMapPointsEqual(initialPoint, selectedPoint)
  const isEditModePristine = mode === 'edit' && !hasSelectedPointChanged
  const isContinueDisabled =
    !addressDraft ||
    !selectedPoint ||
    operation !== 'idle' ||
    isEditModePristine

  return (
    <CreateObjectStepLayout
      {...layoutProps}
      mainClassName={s.main}
      continueDisabled={isContinueDisabled}
      continueLabel={
        layoutProps.continueLabel ??
        t('kyrgyzstan_block.create_object.actions.select')
      }
      continueLoading={operation === 'submitting'}
      onContinue={() => handleContinue(submitTargetStepId)}
      title={t('kyrgyzstan_block.create_object.steps.address_map.title')}
    >
      <div className={s.container}>
        <div className={s.mapWrapper}>
          <div
            ref={mapRef}
            className={s.map}
          />

          <ZoomControls
            handleZoomIn={handleZoomIn}
            handleZoomOut={handleZoomOut}
          />

          {isMapLoading && (
            <div className={s.loadingOverlay}>
              <ImageOptimized
                src={mapBg}
                alt=""
                className={s.mapBg}
              />
              <Loader />
            </div>
          )}

          {mapError && (
            <div className={s.errorOverlay}>
              <p className={clsx(s.errorText, 'medium-14')}>{mapError}</p>
              <Button
                type="button"
                variant="solidBlue"
                className={clsx(s.retryButton, 'semi-bold-14')}
                onClick={handleRetry}
              >
                {t('kyrgyzstan_block.create_object.steps.address_map.retry')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </CreateObjectStepLayout>
  )
}
